import axios from 'axios';
import { handleHttpError } from './errorHandler';

function generateRequestId() {
    if (
        typeof crypto !== 'undefined' &&
        typeof crypto.randomUUID === 'function'
    ) {
        return crypto.randomUUID();
    }
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random()
        .toString(16)
        .slice(2)}`;
}

const isDev = import.meta.env.MODE === 'development';

/**
 * API baseURL 規則（路徑一律是 /api/...）
 * - development：空字串 → Vite proxy 轉 /api
 * - 其他：用 VITE_API_BASE_URL（例如 http://127.0.0.1:8201），不要留空同源，
 *   否則若 Docker nginx 沒轉 /api，GET 會拿到 SPA index.html（POST 則是 405）
 * - 禁止設成 '/api'（會變成 /api/api/...）
 */
function resolveBaseURL() {
    const fromEnv = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
    if (isDev) {
        // 本地 vite：同源 + proxy；若有設 absolute API 亦可直連
        return fromEnv || '';
    }
    return fromEnv;
}

const baseURL = resolveBaseURL();

/**
 * @type {import('axios').AxiosInstance & { POST: Function, GET: Function, PUT: Function, DELETE: Function }}
 */
const apiService = axios.create({
    baseURL,
    timeout: 30000, // 30 秒超時
    // 後台 Passport Bearer，不靠 cookie / XSRF
    withCredentials: false
});

/**
 * 通用請求方法
 */
apiService.request = async function (
    method,
    url,
    data = {},
    customHeaders = {},
    params = {}
) {
    const skipNotification = Boolean(customHeaders.skipNotification);
    try {
        const accessToken = localStorage.getItem('accessToken');
        const headers = {};
        const requestId =
            customHeaders['X-Request-Id'] ||
            customHeaders['x-request-id'] ||
            generateRequestId();
        headers['X-Request-Id'] = requestId;
        headers.Accept = customHeaders.Accept || 'application/json';

        const upperMethod = String(method).toLowerCase();
        if (data instanceof FormData) {
            // FormData：不手動設 Content-Type
        } else if (!['get', 'delete', 'head'].includes(upperMethod)) {
            headers['Content-Type'] = customHeaders.ContentType
                ? customHeaders.ContentType
                : 'application/json';
        } else if (customHeaders.ContentType) {
            headers['Content-Type'] = customHeaders.ContentType;
        }

        const isLoginRequest =
            typeof url === 'string' && url.includes('/admin/login');
        if (!isLoginRequest && accessToken) {
            headers.Authorization = `Bearer ${accessToken}`;
        }
        if (customHeaders.Authorization) {
            headers.Authorization = customHeaders.Authorization;
        }

        const config = {
            method,
            url,
            headers,
            ...(customHeaders.timeout ? { timeout: customHeaders.timeout } : {})
        };

        if (customHeaders.responseType) {
            config.responseType = customHeaders.responseType;
        }

        if (['get', 'delete'].includes(upperMethod)) {
            config.params = params;
        } else {
            config.data = data;
        }

        const response = await this(config);
        const body = response.data;

        // 代理漏設時 SPA 會回 index.html，提早指出
        if (
            typeof body === 'string' &&
            (body.includes('<!doctype html') || body.includes('<!DOCTYPE html'))
        ) {
            const err = new Error(
                'API 回了網頁而非 JSON：請確認 VITE_API_BASE_URL 指向 API（本機通常 http://127.0.0.1:8201），或 nginx 有轉發 /api'
            );
            err.response = response;
            throw err;
        }

        return body;
    } catch (error) {
        if (!error.response) {
            // 這是網路層級的錯誤（CORS、連線失敗、超時等）
            console.error(
                `Network Error for ${method.toUpperCase()} ${url}:`,
                error
            );

            const statusCode = error.request?.status;
            if (statusCode === 413) {
                error.message =
                    '上傳檔案過大，請壓縮圖片後再試（建議小於 2MB）';
                error.response = {
                    status: 413,
                    data: { message: error.message }
                };
            } else if (
                error.code === 'ECONNABORTED' ||
                error.message?.includes('timeout')
            ) {
                error.message = '請求超時，請稍後再試';
            } else if (
                error.message?.includes('fetch') ||
                error.message?.includes('Network')
            ) {
                error.message = '網路連線失敗，請檢查網路連線';
            }
        }

        return handleHttpError(error, url, method, { skipNotification });
    }
};

function createHttpMethod(method) {
    return function (url, dataOrParams = {}, customHeaders = {}) {
        if (['get', 'delete'].includes(method)) {
            return apiService.request(
                method,
                url,
                {},
                customHeaders,
                dataOrParams
            );
        }
        return apiService.request(method, url, dataOrParams, customHeaders);
    };
}

apiService.GET = createHttpMethod('get');
apiService.POST = createHttpMethod('post');
apiService.PUT = createHttpMethod('put');
apiService.PATCH = createHttpMethod('patch');
apiService.DELETE = createHttpMethod('delete');

axios.interceptors.response.use(
    (response) => response,
    (error) => handleHttpError(error)
);

export default apiService;
